import React, { useMemo, useState } from 'react'

import { cn } from '../lib/utils'
import '../styles/participant-room.css'

const KEYS = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B']
const ALL_PITCH_VALUES = Array.from({ length: 25 }, (_, index) => index - 12)
const STEP_CHOICES = [1, 2, 3]
const PROFILE_PRESETS = [
  { label: 'Very Low', value: -6 },
  { label: 'Low', value: -3 },
  { label: 'Original', value: 0 },
  { label: 'High', value: 3 },
  { label: 'Very High', value: 6 }
]
const FAVORITE_VALUES = [-7, -5, -3, -2, 0, 2, 4, 7]

const clampPitch = (value) => Math.max(-12, Math.min(12, value))
const formatPitch = (value) => (value > 0 ? `+${value}` : `${value}`)

const getKeyFromShift = (originalKey, pitchShift) => {
  const originalIndex = KEYS.indexOf(originalKey)
  if (originalIndex === -1) return ''
  const shiftedIndex = (originalIndex + pitchShift + KEYS.length) % KEYS.length
  return KEYS[shiftedIndex]
}

const getNearestShiftToTargetKey = (originalKey, targetKey) => {
  const originalIndex = KEYS.indexOf(originalKey)
  const targetIndex = KEYS.indexOf(targetKey)
  if (originalIndex === -1 || targetIndex === -1) return 0
  const forwardDistance = (targetIndex - originalIndex + KEYS.length) % KEYS.length
  return forwardDistance > 6 ? forwardDistance - 12 : forwardDistance
}

const modalShellClasses =
  'room-elevated room-glow rounded-3xl border border-white/15 bg-[color:rgba(13,23,39,0.96)] p-4 text-[var(--room-text)]'

const buttonBaseClasses =
  'room-focus-ring flex h-11 items-center justify-center rounded-xl border px-3 text-sm font-semibold transition-colors'

const valueChipClasses =
  'rounded-full border border-white/20 bg-black/25 px-3 py-1 text-xs font-medium tracking-[0.02em] text-[var(--room-muted)]'

const ConceptFrame = ({ index, title, description, pitch, currentKey, onReset, children }) => (
  <article className={cn(modalShellClasses, 'relative min-h-[330px]')}>
    <div className="mb-3 flex items-start justify-between gap-3">
      <div>
        <p className="text-xs uppercase tracking-[0.12em] text-[var(--room-muted)]">Concept {index}</p>
        <h2 className="mt-1 text-lg font-semibold">{title}</h2>
        <p className="mt-1 text-sm text-[var(--room-muted)]">{description}</p>
      </div>
      <button
        type="button"
        onClick={onReset}
        className={cn(
          buttonBaseClasses,
          'h-9 rounded-full border-white/20 bg-white/5 px-3 text-xs text-[var(--room-muted)] hover:bg-white/10 hover:text-[var(--room-text)]'
        )}
      >
        Reset
      </button>
    </div>

    <div className="mb-3 flex items-center gap-2">
      <span className={valueChipClasses}>Pitch {formatPitch(pitch)}</span>
      <span className={valueChipClasses}>Key {currentKey}</span>
    </div>

    {children}
  </article>
)

const PitchControlConceptsPage = () => {
  const [originalKey, setOriginalKey] = useState('C')

  const [pitchA, setPitchA] = useState(0)
  const [pitchB, setPitchB] = useState(0)
  const [stepB, setStepB] = useState(1)
  const [targetKeyC, setTargetKeyC] = useState('C')
  const [octaveC, setOctaveC] = useState(0)
  const [pitchD, setPitchD] = useState(0)
  const [targetKeyE, setTargetKeyE] = useState('C')
  const [octaveE, setOctaveE] = useState(0)
  const [pitchF, setPitchF] = useState(0)
  const [historyF, setHistoryF] = useState([0, -2, 2, 5, -5])

  const pitchC = useMemo(
    () => clampPitch(getNearestShiftToTargetKey(originalKey, targetKeyC) + octaveC),
    [originalKey, targetKeyC, octaveC]
  )

  const pitchE = useMemo(
    () => clampPitch(getNearestShiftToTargetKey(originalKey, targetKeyE) + octaveE),
    [originalKey, targetKeyE, octaveE]
  )

  const wheelButtons = useMemo(
    () =>
      KEYS.map((key, index) => {
        const angle = (index / KEYS.length) * Math.PI * 2 - Math.PI / 2
        const radiusPercent = 39
        return {
          key,
          left: `${50 + Math.cos(angle) * radiusPercent}%`,
          top: `${50 + Math.sin(angle) * radiusPercent}%`
        }
      }),
    []
  )

  const commitPitchF = (value) => {
    const clamped = clampPitch(value)
    setPitchF(clamped)
    setHistoryF((previous) => [clamped, ...previous.filter((item) => item !== clamped)].slice(0, 6))
  }

  return (
    <div className="participant-room-theme min-h-[100dvh]">
      <div className="mx-auto w-full max-w-7xl px-4 pb-16 pt-8">
        <header className={cn(modalShellClasses, 'mb-6')}>
          <p className="text-xs uppercase tracking-[0.14em] text-[var(--room-muted)]">Pitch Control Lab</p>
          <h1 className="mt-2 text-2xl font-semibold sm:text-3xl">Six New Modal Concepts</h1>
          <p className="mt-2 max-w-3xl text-sm text-[var(--room-muted)]">
            Each option removes reliance on a drag bar by surfacing quick jumps, direct semitone picks, key-based
            picking, or memory-based shortcuts.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <label className="text-sm text-[var(--room-muted)]" htmlFor="original-key-select">
              Original key
            </label>
            <select
              id="original-key-select"
              value={originalKey}
              onChange={(event) => setOriginalKey(event.target.value)}
              className="room-input room-focus-ring h-10 rounded-xl px-3 text-sm"
            >
              {KEYS.map((key) => (
                <option key={key} value={key}>
                  {key}
                </option>
              ))}
            </select>
          </div>
        </header>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <ConceptFrame
            index={1}
            title="Full Semitone Matrix"
            description="Show every value from -12 to +12 at once. No hidden state, no slider, one-tap selection."
            pitch={pitchA}
            currentKey={getKeyFromShift(originalKey, pitchA)}
            onReset={() => setPitchA(0)}
          >
            <div className="grid grid-cols-5 gap-2">
              {ALL_PITCH_VALUES.map((value) => {
                const isActive = value === pitchA
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setPitchA(value)}
                    className={cn(
                      buttonBaseClasses,
                      isActive
                        ? 'border-[color:var(--room-primary)] bg-[color:rgba(46,168,255,0.2)] text-[var(--room-text)]'
                        : 'border-white/15 bg-white/5 text-[var(--room-muted)] hover:bg-white/10 hover:text-[var(--room-text)]'
                    )}
                  >
                    {formatPitch(value)}
                  </button>
                )
              })}
            </div>
          </ConceptFrame>

          <ConceptFrame
            index={2}
            title="Large Stepper + Jump Sizes"
            description="Two giant controls for muscle memory, with configurable step size and octave jumps."
            pitch={pitchB}
            currentKey={getKeyFromShift(originalKey, pitchB)}
            onReset={() => {
              setPitchB(0)
              setStepB(1)
            }}
          >
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setPitchB((current) => clampPitch(current - stepB))}
                  className={cn(buttonBaseClasses, 'h-14 border-white/20 bg-white/5 text-xl hover:bg-white/10')}
                >
                  -{stepB}
                </button>
                <button
                  type="button"
                  onClick={() => setPitchB((current) => clampPitch(current + stepB))}
                  className={cn(buttonBaseClasses, 'h-14 border-white/20 bg-white/5 text-xl hover:bg-white/10')}
                >
                  +{stepB}
                </button>
              </div>

              <div className="grid grid-cols-3 gap-2">
                {STEP_CHOICES.map((value) => {
                  const isActive = stepB === value
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setStepB(value)}
                      className={cn(
                        buttonBaseClasses,
                        isActive
                          ? 'border-[color:var(--room-accent)] bg-[color:rgba(23,214,163,0.2)] text-[var(--room-text)]'
                          : 'border-white/15 bg-white/5 text-[var(--room-muted)] hover:bg-white/10 hover:text-[var(--room-text)]'
                      )}
                    >
                      Step {value}
                    </button>
                  )
                })}
              </div>

              <div className="grid grid-cols-4 gap-2">
                {[-12, -7, 7, 12].map((jump) => (
                  <button
                    key={jump}
                    type="button"
                    onClick={() => setPitchB((current) => clampPitch(current + jump))}
                    className={cn(
                      buttonBaseClasses,
                      'border-white/15 bg-black/25 text-[var(--room-muted)] hover:bg-white/10 hover:text-[var(--room-text)]'
                    )}
                  >
                    {jump > 0 ? `+${jump}` : jump}
                  </button>
                ))}
              </div>
            </div>
          </ConceptFrame>

          <ConceptFrame
            index={3}
            title="Target Key First"
            description="Pick the desired key instead of semitone math, then choose octave lane."
            pitch={pitchC}
            currentKey={getKeyFromShift(originalKey, pitchC)}
            onReset={() => {
              setTargetKeyC(originalKey)
              setOctaveC(0)
            }}
          >
            <div className="space-y-3">
              <div className="grid grid-cols-6 gap-2">
                {KEYS.map((key) => {
                  const isActive = key === targetKeyC
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setTargetKeyC(key)}
                      className={cn(
                        buttonBaseClasses,
                        isActive
                          ? 'border-[color:var(--room-primary)] bg-[color:rgba(46,168,255,0.2)] text-[var(--room-text)]'
                          : 'border-white/15 bg-white/5 text-[var(--room-muted)] hover:bg-white/10 hover:text-[var(--room-text)]'
                      )}
                    >
                      {key}
                    </button>
                  )
                })}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: 'Down Octave', value: -12 },
                  { label: 'Closest', value: 0 },
                  { label: 'Up Octave', value: 12 }
                ].map((option) => {
                  const isActive = option.value === octaveC
                  return (
                    <button
                      key={option.label}
                      type="button"
                      onClick={() => setOctaveC(option.value)}
                      className={cn(
                        buttonBaseClasses,
                        isActive
                          ? 'border-[color:var(--room-accent)] bg-[color:rgba(23,214,163,0.2)] text-[var(--room-text)]'
                          : 'border-white/15 bg-white/5 text-[var(--room-muted)] hover:bg-white/10 hover:text-[var(--room-text)]'
                      )}
                    >
                      {option.label}
                    </button>
                  )
                })}
              </div>
            </div>
          </ConceptFrame>

          <ConceptFrame
            index={4}
            title="Range Presets + Fine Tune"
            description="Start from comfort profiles, then nudge with tiny controls to dial in exact fit."
            pitch={pitchD}
            currentKey={getKeyFromShift(originalKey, pitchD)}
            onReset={() => setPitchD(0)}
          >
            <div className="space-y-3">
              <div className="grid grid-cols-5 gap-2">
                {PROFILE_PRESETS.map((preset) => {
                  const isActive = pitchD === preset.value
                  return (
                    <button
                      key={preset.label}
                      type="button"
                      onClick={() => setPitchD(preset.value)}
                      className={cn(
                        buttonBaseClasses,
                        isActive
                          ? 'border-[color:var(--room-primary)] bg-[color:rgba(46,168,255,0.2)] text-[var(--room-text)]'
                          : 'border-white/15 bg-white/5 text-[var(--room-muted)] hover:bg-white/10 hover:text-[var(--room-text)]'
                      )}
                    >
                      {preset.label}
                    </button>
                  )
                })}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setPitchD((current) => clampPitch(current - 1))}
                  className={cn(buttonBaseClasses, 'h-12 border-white/20 bg-white/5 text-lg hover:bg-white/10')}
                >
                  -1
                </button>
                <button
                  type="button"
                  onClick={() => setPitchD(0)}
                  className={cn(
                    buttonBaseClasses,
                    'h-12 border-white/20 bg-black/25 text-[var(--room-muted)] hover:bg-white/10 hover:text-[var(--room-text)]'
                  )}
                >
                  Original
                </button>
                <button
                  type="button"
                  onClick={() => setPitchD((current) => clampPitch(current + 1))}
                  className={cn(buttonBaseClasses, 'h-12 border-white/20 bg-white/5 text-lg hover:bg-white/10')}
                >
                  +1
                </button>
              </div>
            </div>
          </ConceptFrame>

          <ConceptFrame
            index={5}
            title="Key Wheel"
            description="A radial selector for quick tonal jumps with optional octave lane."
            pitch={pitchE}
            currentKey={getKeyFromShift(originalKey, pitchE)}
            onReset={() => {
              setTargetKeyE(originalKey)
              setOctaveE(0)
            }}
          >
            <div className="space-y-3">
              <div className="relative mx-auto h-64 w-64 rounded-full border border-white/15 bg-black/20">
                <div className="absolute left-1/2 top-1/2 flex h-24 w-24 -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full border border-white/15 bg-[color:rgba(16,27,45,0.96)]">
                  <p className="text-xs uppercase tracking-[0.08em] text-[var(--room-muted)]">Pitch</p>
                  <p className="text-xl font-semibold">{formatPitch(pitchE)}</p>
                </div>

                {wheelButtons.map((item) => {
                  const isActive = item.key === targetKeyE
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setTargetKeyE(item.key)}
                      style={{ left: item.left, top: item.top }}
                      className={cn(
                        buttonBaseClasses,
                        'absolute h-10 w-10 -translate-x-1/2 -translate-y-1/2 rounded-full p-0 text-xs',
                        isActive
                          ? 'border-[color:var(--room-primary)] bg-[color:rgba(46,168,255,0.2)] text-[var(--room-text)]'
                          : 'border-white/20 bg-[color:rgba(16,27,45,0.95)] text-[var(--room-muted)] hover:bg-white/10 hover:text-[var(--room-text)]'
                      )}
                    >
                      {item.key}
                    </button>
                  )
                })}
              </div>

              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: '-12', value: -12 },
                  { label: '0', value: 0 },
                  { label: '+12', value: 12 }
                ].map((item) => {
                  const isActive = octaveE === item.value
                  return (
                    <button
                      key={item.label}
                      type="button"
                      onClick={() => setOctaveE(item.value)}
                      className={cn(
                        buttonBaseClasses,
                        isActive
                          ? 'border-[color:var(--room-accent)] bg-[color:rgba(23,214,163,0.2)] text-[var(--room-text)]'
                          : 'border-white/15 bg-white/5 text-[var(--room-muted)] hover:bg-white/10 hover:text-[var(--room-text)]'
                      )}
                    >
                      Octave {item.label}
                    </button>
                  )
                })}
              </div>
            </div>
          </ConceptFrame>

          <ConceptFrame
            index={6}
            title="Favorites + Recent Memory"
            description="Fast access to common intervals and your latest choices. Great for repeated songs."
            pitch={pitchF}
            currentKey={getKeyFromShift(originalKey, pitchF)}
            onReset={() => {
              setPitchF(0)
              setHistoryF([0, -2, 2, 5, -5])
            }}
          >
            <div className="space-y-3">
              <div>
                <p className="mb-2 text-xs uppercase tracking-[0.1em] text-[var(--room-muted)]">Favorites</p>
                <div className="grid grid-cols-4 gap-2">
                  {FAVORITE_VALUES.map((value) => {
                    const isActive = value === pitchF
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => commitPitchF(value)}
                        className={cn(
                          buttonBaseClasses,
                          isActive
                            ? 'border-[color:var(--room-primary)] bg-[color:rgba(46,168,255,0.2)] text-[var(--room-text)]'
                            : 'border-white/15 bg-white/5 text-[var(--room-muted)] hover:bg-white/10 hover:text-[var(--room-text)]'
                        )}
                      >
                        {formatPitch(value)}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div>
                <p className="mb-2 text-xs uppercase tracking-[0.1em] text-[var(--room-muted)]">Recent</p>
                <div className="flex flex-wrap gap-2">
                  {historyF.map((value) => (
                    <button
                      key={`history-${value}`}
                      type="button"
                      onClick={() => commitPitchF(value)}
                      className={cn(
                        buttonBaseClasses,
                        'h-9 rounded-full border-white/20 bg-black/25 px-3 text-xs text-[var(--room-muted)] hover:bg-white/10 hover:text-[var(--room-text)]'
                      )}
                    >
                      {formatPitch(value)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => commitPitchF(pitchF - 1)}
                  className={cn(buttonBaseClasses, 'h-12 border-white/20 bg-white/5 text-lg hover:bg-white/10')}
                >
                  -1 semitone
                </button>
                <button
                  type="button"
                  onClick={() => commitPitchF(pitchF + 1)}
                  className={cn(buttonBaseClasses, 'h-12 border-white/20 bg-white/5 text-lg hover:bg-white/10')}
                >
                  +1 semitone
                </button>
              </div>
            </div>
          </ConceptFrame>
        </div>

        <section className={cn(modalShellClasses, 'mt-4')}>
          <h2 className="text-base font-semibold">Quick Comparison Notes</h2>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-[var(--room-muted)]">
            <li>Concept 1 maximizes speed for users who know the exact semitone.</li>
            <li>Concept 2 is strongest for one-handed use and repetitive nudging.</li>
            <li>Concepts 3 and 5 work best for users who think in keys, not semitone numbers.</li>
            <li>Concept 4 reduces decision fatigue with comfort-first presets.</li>
            <li>Concept 6 is strongest when users repeat familiar transpositions across songs.</li>
          </ul>
        </section>
      </div>
    </div>
  )
}

export default PitchControlConceptsPage
